import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "form",
    "saveStatus",
    "autoSaveStatus",
    "statusText",
    "statusIcon",
  ];

  selectprocedure = (e) => {
    console.log("SelectProcedure clicked");
    let parent = e.target.parentNode.parentNode;
    let to_unhide = parent.getElementsByClassName("procedure-fields")[0];
    let to_remove = parent.getElementsByClassName("medication-fields")[0];
    let to_set = parent.getElementsByClassName("type-field")[0];

    console.log("Found medication-fields to remove:", !!to_remove);
    console.log("Found procedure-fields to unhide:", !!to_unhide);

    // Before removing, also clear any duplicate acr_code inputs
    const acrCodeInputs = parent.querySelectorAll('input[name*="[acr_code]"]');
    console.log("Found acr_code inputs:", acrCodeInputs.length);
    acrCodeInputs.forEach((input) => {
      if (input.closest(".medication-fields")) {
        console.log("Clearing medication acr_code input value:", input.value);
        input.value = ""; // Clear the value before removal
      }
    });

    if (to_remove) {
      to_remove.remove();
      console.log("Removed medication-fields");
    }
    if (to_unhide) {
      to_unhide.classList.remove("hidden");
      console.log("Unhid procedure-fields");
    }
    e.target.parentNode.classList.add("hidden");
    if (to_set) {
      to_set.value = "procedure";
      console.log("Set type to procedure");
    }
  };

  selectmedication = (e) => {
    console.log("SelectMedication clicked");
    let parent = e.target.parentNode.parentNode;
    let to_unhide = parent.getElementsByClassName("medication-fields")[0];
    let to_remove = parent.getElementsByClassName("procedure-fields")[0];
    let to_set = parent.getElementsByClassName("type-field")[0];

    console.log("Found procedure-fields to remove:", !!to_remove);
    console.log("Found medication-fields to unhide:", !!to_unhide);

    // Before removing, also clear any duplicate acr_code inputs
    const acrCodeInputs = parent.querySelectorAll('input[name*="[acr_code]"]');
    console.log("Found acr_code inputs:", acrCodeInputs.length);
    acrCodeInputs.forEach((input) => {
      if (input.closest(".procedure-fields")) {
        console.log("Clearing procedure acr_code input value:", input.value);
        input.value = ""; // Clear the value before removal
      }
    });

    if (to_remove) {
      to_remove.remove();
      console.log("Removed procedure-fields");
    }
    if (to_unhide) {
      to_unhide.classList.remove("hidden");
      console.log("Unhid medication-fields");
    }
    e.target.parentNode.classList.add("hidden");
    if (to_set) {
      to_set.value = "medication";
      console.log("Set type to medication");
    }
  };

  connect() {
    console.log("Field encounter controller connected");

    // Initialize auto-save state (same as patient forms)
    this.autoSaveRetryCount = 0;
    this.maxRetries = 3;
    this.retryDelay = 5000; // 5 seconds
    this.lastSaveTime = null;
    this.isAutoSaving = false;

    // Initialize status - hidden initially until first save
    if (this.hasAutoSaveStatusTarget) {
      this.autoSaveStatusTarget.style.display = "none";
    }

    // Only start auto-save and change tracking if we have a form target
    if (this.hasFormTarget) {
      // Start auto-save timer
      this.startAutoSave();

      // Set up form change tracking
      this.setupChangeTracking();
    }
  }

  disconnect() {
    console.log(
      "Field encounter controller disconnected - cleaning up auto-save timer"
    );
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  setupChangeTracking() {
    // Only set up change tracking if we have a form target
    if (!this.hasFormTarget) {
      return;
    }

    // Track changes to the form
    this.formTarget.addEventListener("input", () => {
      this.markFormAsChanged();
    });

    this.formTarget.addEventListener("change", () => {
      this.markFormAsChanged();
    });
  }

  markFormAsChanged() {
    this.hasChanges = true;
    this.updateAutoSaveStatus("warning", "Unsaved changes", "#ffc107");
  }

  startAutoSave() {
    // Auto-save every 30 seconds
    this.autoSaveTimer = setInterval(() => {
      if (this.hasChanges) {
        this.autoSave();
      }
    }, 30000);
  }

  async autoSave() {
    if (this.isAutoSaving) {
      console.log("Auto-save already in progress, skipping...");
      return Promise.resolve();
    }

    console.log("Starting auto-save...");
    this.isAutoSaving = true;
    this.updateAutoSaveStatus("saving", "Saving your progress", "#ffc107");

    try {
      // Get the field encounter ID from the form action or URL
      const fieldEncounterId = this.getFieldEncounterId();
      if (!fieldEncounterId) {
        console.error("Could not determine field encounter ID for auto-save");
        throw new Error("Could not determine field encounter ID");
      }

      // Prepare form data
      const formData = new FormData(this.formTarget);

      // Send auto-save request
      const response = await fetch(
        `/field_encounters/${fieldEncounterId}/auto_save`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (result.success) {
        console.log("Auto-save successful:", result.message);

        // Success - reset retry count and update status
        this.autoSaveRetryCount = 0;
        this.hasChanges = false;
        this.lastSaveTime = new Date();
        this.updateAutoSaveStatus(
          "success",
          this.formatLastSavedTime(),
          "#28a745"
        );

        // Update form fields with new database IDs
        if (result.id_mappings) {
          this.updateFormFieldsWithDatabaseIds(result.id_mappings);
        }

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          if (
            this.statusTextTarget.textContent === this.formatLastSavedTime()
          ) {
            this.updateAutoSaveStatus("ready", "Ready to save", "#28a745");
          }
        }, 3000);

        return result;
      } else {
        console.error("Auto-save failed:", result.errors || result.error);
        throw new Error(result.errors || result.error);
      }
    } catch (error) {
      console.error("Auto-save error:", error);
      this.handleAutoSaveError(error);
      throw error;
    } finally {
      this.isAutoSaving = false;
    }
  }

  handleAutoSaveError(error) {
    this.autoSaveRetryCount++;

    if (this.autoSaveRetryCount <= this.maxRetries) {
      console.log(
        `Auto-save failed, retrying in ${this.retryDelay}ms (attempt ${this.autoSaveRetryCount}/${this.maxRetries})`
      );
      this.updateAutoSaveStatus(
        "error",
        `Retrying save (${this.autoSaveRetryCount}/${this.maxRetries})...`,
        "#ffc107"
      );

      this.retryTimeout = setTimeout(() => {
        this.autoSave();
      }, this.retryDelay);
    } else {
      console.error("Auto-save failed after maximum retries");
      this.updateAutoSaveStatus(
        "error",
        "Save failed - check connection",
        "#dc3545"
      );

      // Reset retry count after showing error for a while
      setTimeout(() => {
        this.autoSaveRetryCount = 0;
        if (this.hasAutoSaveStatusTarget) {
          this.autoSaveStatusTarget.style.display = "none";
        }
      }, 10000); // Wait 10 seconds before hiding and allowing retries again
    }
  }

  getFieldEncounterId() {
    // Try to extract from form action URL
    const action = this.formTarget.action;
    const match = action.match(/\/field_encounters\/(\d+)/);
    if (match) {
      return match[1];
    }

    // Try to extract from current URL
    const urlMatch = window.location.pathname.match(
      /\/field_encounters\/(\d+)/
    );
    if (urlMatch) {
      return urlMatch[1];
    }

    return null;
  }

  updateStatus(text, type) {
    this.statusTextTarget.textContent = text;

    // Update icon color based on type
    const icon = this.statusIconTarget;
    icon.className = "fa fa-circle";

    switch (type) {
      case "ready":
        icon.style.color = "#28a745"; // green
        break;
      case "warning":
        icon.style.color = "#ffc107"; // yellow
        break;
      case "saving":
        icon.style.color = "#007bff"; // blue
        icon.className = "fa fa-spinner fa-spin";
        break;
      case "success":
        icon.style.color = "#28a745"; // green
        break;
      case "error":
        icon.style.color = "#dc3545"; // red
        break;
    }
  }

  updateAutoSaveStatus(status, message, iconColor) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = message;
    }
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.style.color = iconColor;

      // Update icon based on status
      this.statusIconTarget.className = "fa";
      if (status === "saving") {
        this.statusIconTarget.classList.add("fa-spinner", "fa-spin");
      } else if (status === "error") {
        this.statusIconTarget.classList.add("fa-exclamation-triangle");
      } else if (status === "success") {
        this.statusIconTarget.classList.add("fa-check-circle");
      } else {
        this.statusIconTarget.classList.add("fa-circle");
      }
    }

    // Show the status indicator when updating
    if (this.hasAutoSaveStatusTarget) {
      this.autoSaveStatusTarget.style.display = "block";
    }
  }

  formatLastSavedTime() {
    if (!this.lastSaveTime) return "Never saved";

    const now = new Date();
    const diffInMinutes = Math.floor((now - this.lastSaveTime) / (1000 * 60));

    if (diffInMinutes < 1) {
      return "Just saved";
    } else if (diffInMinutes < 60) {
      return `Saved ${diffInMinutes}m ago`;
    } else {
      const diffInHours = Math.floor(diffInMinutes / 60);
      return `Saved ${diffInHours}h ago`;
    }
  }

  updateFormFieldsWithDatabaseIds(idMappings) {
    console.log("=== Updating form fields with database IDs ===");
    console.log("ID mappings received:", idMappings);

    // Update vital signs and field procedures ID fields
    for (const [timestampId, databaseId] of Object.entries(idMappings)) {
      if (databaseId === null || databaseId === undefined) {
        continue; // Skip if no database ID was assigned
      }

      console.log(
        `Updating timestamp ID ${timestampId} with database ID ${databaseId}`
      );

      // Find vital sign ID fields with this timestamp ID
      const vitalSignIdFields = this.formTarget.querySelectorAll(
        `input[name*="[vital_signs_attributes]"][name*="[${timestampId}]"][name$="[id]"]`
      );

      vitalSignIdFields.forEach((field) => {
        console.log(
          `Found vital sign ID field: ${field.name}, updating value from ${field.value} to ${databaseId}`
        );
        field.value = databaseId;
      });

      // Find field procedure ID fields with this timestamp ID
      const procedureIdFields = this.formTarget.querySelectorAll(
        `input[name*="[field_procedures_attributes]"][name*="[${timestampId}]"][name$="[id]"]`
      );

      procedureIdFields.forEach((field) => {
        console.log(
          `Found procedure ID field: ${field.name}, updating value from ${field.value} to ${databaseId}`
        );
        field.value = databaseId;
      });

      // Also update any field names that contain the timestamp ID
      // This handles the case where the field names themselves contain the timestamp
      const allFields = this.formTarget.querySelectorAll(
        `input[name*="[${timestampId}]"], select[name*="[${timestampId}]"], textarea[name*="[${timestampId}]"]`
      );

      allFields.forEach((field) => {
        const oldName = field.name;
        const newName = oldName.replace(`[${timestampId}]`, `[${databaseId}]`);
        if (oldName !== newName) {
          console.log(`Updating field name from ${oldName} to ${newName}`);
          field.name = newName;
        }

        // Also update the id attribute if it contains the timestamp ID
        const oldId = field.id;
        const newId = oldId.replace(timestampId, databaseId);
        if (oldId !== newId) {
          console.log(`Updating field id from ${oldId} to ${newId}`);
          field.id = newId;
        }
      });
    }

    console.log("=== Finished updating form fields ===");
  }

  submit(event) {
    console.log("Form submitted manually");
    this.hasChanges = false;
    // Let the normal form submission proceed
  }

  // Method to sort vital signs and procedures by time
  sortByTime() {
    console.log("Sorting vital signs and procedures by time");

    // Sort vital signs
    this.sortSectionByTime("#vital-signs", 'input[name*="[time]"]');

    // Sort procedures
    this.sortSectionByTime(".field-procedures", 'input[name*="[time]"]');

    // Show feedback
    this.showMessage("Sorted by time", "success");
  }

  sortSectionByTime(sectionSelector, timeInputSelector) {
    const section = this.formTarget.querySelector(sectionSelector);
    if (!section) {
      console.error(`Section not found: ${sectionSelector}`);
      return;
    }

    // Get all nested field containers
    const containers = Array.from(section.querySelectorAll(".nested-fields"));

    if (containers.length === 0) {
      console.log(`No items to sort in ${sectionSelector}`);
      return;
    }

    // Sort containers by time
    containers.sort((a, b) => {
      const timeA = this.getTimeValue(a, timeInputSelector);
      const timeB = this.getTimeValue(b, timeInputSelector);

      // Empty times go to the end
      if (!timeA && !timeB) return 0;
      if (!timeA) return 1;
      if (!timeB) return -1;

      return timeA.localeCompare(timeB);
    });

    // Get the parent container where sorted items should be inserted
    const parentContainer = containers[0].parentNode;

    // Remove all containers from the DOM
    containers.forEach((container) => container.remove());

    // Re-insert in sorted order, before the "links" section
    const linksSection = section.querySelector(
      '[data-target="nested-form.links"], .links'
    );
    containers.forEach((container) => {
      if (linksSection) {
        parentContainer.insertBefore(container, linksSection);
      } else {
        parentContainer.appendChild(container);
      }
    });

    console.log(`Sorted ${containers.length} items in ${sectionSelector}`);
  }

  getTimeValue(container, timeInputSelector) {
    const timeInput = container.querySelector(timeInputSelector);
    if (!timeInput || !timeInput.value) return "";

    // Normalize time format for comparison (handle hh:mm, h:mm, etc.)
    const timeValue = timeInput.value.trim();
    if (timeValue.match(/^\d{1,2}:\d{2}$/)) {
      // Pad single digit hours with leading zero for proper sorting
      const parts = timeValue.split(":");
      return parts[0].padStart(2, "0") + ":" + parts[1];
    }

    return timeValue;
  }

  showMessage(message, type = "info") {
    console.log(`[${type}] ${message}`);

    // If we have a saveStatus target, update it
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = message;
      this.saveStatusTarget.classList.remove("d-none");

      // Add appropriate class based on type
      this.saveStatusTarget.classList.remove(
        "text-success",
        "text-danger",
        "text-info"
      );
      this.saveStatusTarget.classList.add(`text-${type}`);

      // Hide after 2 seconds
      setTimeout(() => {
        this.saveStatusTarget.classList.add("d-none");
      }, 2000);
    } else {
      // Fallback: temporarily show message in auto-save status
      if (this.hasStatusTextTarget) {
        const originalText = this.statusTextTarget.textContent;
        this.statusTextTarget.textContent = message;

        setTimeout(() => {
          this.statusTextTarget.textContent = originalText;
        }, 2000);
      }
    }
  }

  // Methods to handle procedure/medication selection
  selectprocedure(event) {
    event.preventDefault();
    const container = event.target.closest(".nested-fields");
    if (container) {
      container.querySelector(".select-fields").style.display = "none";
      container.querySelector(".procedure-fields").style.display = "block";
      container.querySelector(".procedure-fields").classList.remove("hidden");
      container.querySelector(".medication-fields").style.display = "none";
      container.querySelector(".medication-fields").classList.add("hidden");

      // Set the procedure type
      const typeField = container.querySelector(".type-field");
      if (typeField) {
        typeField.value = "procedure";
      }
    }
  }

  selectmedication(event) {
    event.preventDefault();
    const container = event.target.closest(".nested-fields");
    if (container) {
      container.querySelector(".select-fields").style.display = "none";
      container.querySelector(".medication-fields").style.display = "block";
      container.querySelector(".medication-fields").classList.remove("hidden");
      container.querySelector(".procedure-fields").style.display = "none";
      container.querySelector(".procedure-fields").classList.add("hidden");

      // Set the procedure type
      const typeField = container.querySelector(".type-field");
      if (typeField) {
        typeField.value = "medication";
      }
    }
  }

  fieldentry = (e) => {
    $("#lookup").modal("show");
    console.log("Youre in", e.target);
    const element = e.target;
    let words = element.id.split("_");
    let subcategory = words[1];
    let url_subcat = "";
    if (subcategory == "location") {
      url_subcat = "A-Location";
    } else if (subcategory == "type") {
      url_subcat = "B-Type";
    } else if (subcategory == "mechanism") {
      url_subcat = "C-Mechanism";
    } else {
      return;
    }
    console.log("words", words);
    e.target.value = "";
    let url = `/patient_care_form_codes/${url_subcat}/get_by_subcategory`;
    fetch(url)
      .then((response) => response.json())
      .then((json) => {
        let ul = document.getElementById("code-list");
        console.log("UL: ", ul);
        ul.innerHTML = "";
        json.codes.forEach((code) => {
          let list_item = document.createElement("li");
          list_item.className = "list-group-item pf-code-list";
          list_item.setAttribute("data-action", "click->patient-form#select");
          list_item.setAttribute("data-code", `${code.code}`);
          list_item.setAttribute("data-attr", `${e.target.id}`);
          list_item.innerHTML = `${code.code} | ${code.name}`;
          ul.appendChild(list_item);
        });
        console.log(json.codes);
      });
  };

  select = (e) => {
    console.log("SELECT", e.target.getAttribute("data-code"));
    let field = document.getElementById(e.target.getAttribute("data-attr"));
    console.log("FIELD", field);
    field.value = e.target.getAttribute("data-code");
    $("#lookup").modal("hide");
  };
}

// Global functions for polished procedure interface
window.toggleProcedureEdit = function (procedureId) {
  console.log("Toggling edit mode for procedure:", procedureId);

  const container = document.querySelector(`[data-id="${procedureId}"]`);
  if (!container) {
    console.error("Container not found for procedure ID:", procedureId);
    return;
  }

  const displayMode = container.querySelector(".procedure-display-mode");
  const editMode = container.querySelector(".procedure-edit-mode");

  if (displayMode && editMode) {
    displayMode.style.display = "none";
    editMode.style.display = "block";
  }
};

window.cancelProcedureEdit = function (procedureId) {
  console.log("Closing procedure edit for:", procedureId);

  const container = document.querySelector(`[data-id="${procedureId}"]`);
  if (!container) {
    console.error("Container not found for procedure ID:", procedureId);
    return;
  }

  // Switch back to display mode
  const displayMode = container.querySelector(".procedure-display-mode");
  const editMode = container.querySelector(".procedure-edit-mode");

  if (displayMode && editMode) {
    displayMode.style.display = "block";
    editMode.style.display = "none";
  }
};

function updateProcedureDisplayValues(procedureId) {
  const container = document.querySelector(`[data-id="${procedureId}"]`);
  if (!container) return;

  // Get form values
  const timeInput = container.querySelector('input[name*="[time]"]');
  const acrCodeSelect = container.querySelector('select[name*="[acr_code]"]');
  const routeSelect = container.querySelector('select[name*="[route]"]');
  const resultInput = container.querySelector('input[name*="[result]"]');
  const byWhoRadios = container.querySelectorAll(
    'input[name*="[by_who]"]:checked'
  );

  // Get display elements
  const displayRow = container.querySelector(".procedure-display-row");
  if (!displayRow) return;

  const displayCells = displayRow.querySelectorAll(
    ".col-sm-1, .col-sm-2, .col-sm-4"
  );

  if (displayCells.length >= 5) {
    // Update time
    displayCells[0].textContent = timeInput?.value || "—";

    // Update procedure/medication
    const acrValue =
      acrCodeSelect?.options[acrCodeSelect.selectedIndex]?.text ||
      acrCodeSelect?.value;
    displayCells[1].textContent = acrValue || "—";

    // Update route
    const routeValue =
      routeSelect?.options[routeSelect.selectedIndex]?.text ||
      routeSelect?.value;
    displayCells[2].textContent = routeValue || "—";

    // Update result
    displayCells[3].textContent = resultInput?.value || "—";

    // Update by who
    const byWhoValue = byWhoRadios.length > 0 ? byWhoRadios[0].value : "";
    displayCells[4].textContent = byWhoValue || "—";
  }
}

window.toggleVitalSignEdit = function (vitalSignId) {
  console.log("Toggling edit mode for vital sign:", vitalSignId);

  const container = document.querySelector(`[data-id="${vitalSignId}"]`);
  if (!container) {
    console.error("Container not found for vital sign ID:", vitalSignId);
    return;
  }

  const displayMode = container.querySelector(".vital-sign-display-mode");
  const editMode = container.querySelector(".vital-sign-edit-mode");

  if (displayMode && editMode) {
    displayMode.style.display = "none";
    editMode.style.display = "block";
  }
};
